import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { STORAGE_KEYS } from '@vvf/shared';
import { registerUserUpdateCallback, bootstrapAuth, setAccessToken } from '@/services/api';
import { isInNativeWrapper } from '@/services/nativeBridge';

/**
 * AuthContext globale per la PWA (audit #8). Single source of truth per
 * l'utente loggato: prima 22 file leggevano `localStorage.getItem('vvf.user')`
 * direttamente; un aggiornamento da SettingsPage non si propagava ai
 * componenti gia' montati. Adesso `setUser(...)` aggiorna React state
 * + localStorage atomicamente e tutti i `useAuth()` consumer si
 * re-renderizzano automaticamente.
 *
 * Persistenza: localStorage (key 'vvf.user') per sopravvivere a reload.
 * Sync con SecureStore nativo: rimane responsabilita' di nativeBridge.ts
 * (notifyNativeUserUpdate) che viene chiamato in setUser quando in wrapper.
 */
const USER_KEY = STORAGE_KEYS.USER;

export interface VvfUser {
  id: string;
  email: string;
  nickname: string;
  homeTenantId?: number;
  roles?: string[];
  approved?: boolean;
  bypassVolume?: boolean;
  /** F19: true se l'utente ha una password temporanea (import) e DEVE cambiarla
   * prima di poter usare l'app (gate in DashboardLayout). */
  mustChangePassword?: boolean;
  [key: string]: unknown;
}

interface AuthContextValue {
  user: VvfUser | null;
  /**
   * Item #6: true finche' il silent-refresh al boot (cookie mode) non e'
   * concluso. I gate di routing (WelcomePage/LoginPage) devono attendere
   * `booting === false` prima di decidere se l'utente e' loggato: l'access
   * token vive in memoria e va prima ricostruito dal cookie httpOnly.
   */
  booting: boolean;
  /** Aggiorna l'utente in React state + localStorage. Passa null per logout. */
  setUser: (user: VvfUser | null) => void;
  /** Helper: utente ha uno dei ruoli specificati? */
  hasRole: (...roles: string[]) => boolean;
  /** Reload da localStorage (utile dopo che un altro tab ha modificato). */
  reloadFromStorage: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Il backend serializza il distaccamento dell'utente come `tenantId`
 * (UserInfo.TenantId), mentre il frontend usa `homeTenantId`. Senza questa
 * normalizzazione `user.homeTenantId` resta sempre undefined (mascherato
 * dall'index signature di VvfUser), rompendo la pre-selezione del filtro
 * distaccamento in Gestione Utenti e il nome distaccamento in
 * header/magazzino/scadenze/my-tenant.
 */
function normalizeUser(u: VvfUser | null): VvfUser | null {
  if (!u || u.homeTenantId != null) return u;
  const tid = u.tenantId;
  return typeof tid === 'number' ? { ...u, homeTenantId: tid } : u;
}

function loadUserFromStorage(): VvfUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? normalizeUser(JSON.parse(raw) as VvfUser) : null;
  } catch { return null; }
}

export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [user, setUserState] = useState<VvfUser | null>(() => loadUserFromStorage());
  // Item #6: in cookie mode partiamo "booting" finche' il silent-refresh non
  // ha tentato di ricostruire l'access token dal cookie httpOnly. In wrapper
  // mode la sessione e' gia' in localStorage (rehydrate nativo): nessuna
  // attesa, booting parte gia' false.
  const [booting, setBooting] = useState<boolean>(() => !isInNativeWrapper());

  // Sync cross-tab: se un altro tab fa logout/login/update, ascolto storage.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== USER_KEY) return;
      const next = loadUserFromStorage();
      setUserState(next);
      // Fix minore review (multi-tab): se un altro tab ha fatto LOGOUT (vvf.user
      // rimosso), azzero anche l'access token in memoria di QUESTO tab — in
      // cookie mode vive in memoria, quindi lo storage event da solo non lo
      // ripulisce — e redirigo al login (no flash di pagine protette).
      if (!next) {
        setAccessToken(null);
        const p = window.location.pathname;
        if (!p.startsWith('/login') && !p.startsWith('/hydrants-public')) {
          window.location.href = '/login';
        }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setUser = useCallback((rawNext: VvfUser | null) => {
    const next = normalizeUser(rawNext);
    if (next) {
      localStorage.setItem(USER_KEY, JSON.stringify(next));
    } else {
      localStorage.removeItem(USER_KEY);
    }
    setUserState(next);
  }, []);

  // Item #6: silent-refresh al boot (solo cookie mode). Al reload pagina
  // l'access token in memoria e' perso; il refresh token e' nel cookie
  // httpOnly. bootstrapAuth() tenta un refresh: se va, il backend ritorna
  // anche `user` (via registerUserUpdateCallback -> setUser, gia' registrato
  // sotto). Se fallisce, l'utente non e' loggato e i gate mostrano il login.
  // In wrapper mode bootstrapAuth e' no-op e booting parte gia' false.
  useEffect(() => {
    if (isInNativeWrapper()) { setBooting(false); return; }
    let active = true;
    void bootstrapAuth().finally(() => { if (active) setBooting(false); });
    return () => { active = false; };
  }, []);

  // Audit techlead #3: api.ts (axios refresh) chiama questa callback
  // ogni volta che il backend ritorna un user aggiornato (es. roles cambiati,
  // approved, bypassVolume). Senza, lo state React restava stale fino al
  // prossimo full reload.
  useEffect(() => {
    registerUserUpdateCallback((raw: Record<string, unknown>) => {
      const updated = raw as VvfUser;
      if (updated && typeof updated.id === 'string') {
        setUser(updated);
      }
    });
    return () => registerUserUpdateCallback(null);
  }, [setUser]);

  const hasRole = useCallback((...roles: string[]) => {
    const userRoles = user?.roles ?? [];
    return roles.some(r => userRoles.includes(r));
  }, [user]);

  const reloadFromStorage = useCallback(() => {
    setUserState(loadUserFromStorage());
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, booting, setUser, hasRole, reloadFromStorage }),
    [user, booting, setUser, hasRole, reloadFromStorage],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve essere usato dentro <AuthProvider>');
  return ctx;
}
